import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
} from '@mui/material';
import { Row, Col } from 'antd';
import { Save as SaveIcon, Cancel as CancelIcon, Translate as TranslateIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { MenuService } from '../../../api/menuService';
import { MenuItemCreateRequest, MenuItemUpdateRequest } from '../../../@type/menu.types';
import ActionButton from '../../../components/common/buttons/ActionButton';
import CancelButton from '../../../components/common/buttons/CancelButton';
import Card from '../../../components/common/Card';
import LabeledInput from '../../../components/common/fields/LabeledInput';
import { toast } from 'react-toastify';
import { useBreadcrumbLabel } from "../../../components/breadcrumb/BreadcrumbContext";
import ManageTranslationModal, {
  ManageTranslationItem,
} from '../../../components/common/ManageTranslationModal';
import { useLanguages } from '../../../hooks/useLanguages';
import api from '../../../hooks/api';
import { pathAPI } from '../../../utils/pathApi';

interface AutoTranslateResponse {
  data?: {
    translations?: Record<string, string>;
  };
}

const MenuForm = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  // Route id whose data currently sits in formData; gates translation hydration to it.
  const [loadedMenuId, setLoadedMenuId] = useState<number | null>(null);
  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [{ data: languages }] = useLanguages();
  const [formData, setFormData] = useState<MenuItemCreateRequest>({
    code: '',
    icon_path: '',
    route_name: '',
    sort_order: 0,
    is_active: true,
    requires_permission: '',
    translations: {
      en: { name: '', description: '' },
      vi: { name: '', description: '' },
      zh: { name: '', description: '' },
    },
  });

  useBreadcrumbLabel(isEdit ? formData.translations?.en?.name || formData.code : undefined, 1);

  const loadMenu = useCallback(async (menuId: number) => {
    setLoading(true);
    try {
      const response = await MenuService.getMenuItemById(menuId);
      console.log('Load menu response:', response.data);
      // Server returns { success, code, data: {...} }
      // Access actual menu data from response.data.data
      if (response.data?.data) {
        setFormData(response.data.data);
        setLoadedMenuId(menuId);
      }
    } catch (error) {
      console.error('Failed to load menu:', error);
      toast.error('Failed to load menu item');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      loadMenu(Number(id));
    }
  }, [id, isEdit, loadMenu]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.keys(formData.translations || {}).length === 0) {
      toast.error('Please add at least one translation');
      return;
    }
    setLoading(true);

    try {
      if (isEdit && id) {
        await MenuService.updateMenuItem(Number(id), formData as MenuItemUpdateRequest);
        toast.success(t('menus.updateSuccess'));
      } else {
        await MenuService.createMenuItem(formData);
        toast.success(t('menus.createSuccess'));
      }
      navigate('/settings/menus');
    } catch (error) {
      console.error('Failed to save menu:', error);
      toast.error('Failed to save menu item');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof MenuItemCreateRequest) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value: any;
    if (e.target.type === 'checkbox') {
      value = e.target.checked;
    } else if (e.target.type === 'number') {
      value = Number(e.target.value);
    } else {
      value = e.target.value;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const translationItems: ManageTranslationItem[] = (languages || []).map((language) => {
    const translation = formData.translations?.[language.language_code];
    return {
      translation_id: null,
      translation_name: translation?.name || '',
      translation_description: translation?.description || '',
      language_id: language.id,
      language_code: language.language_code,
      language_name: language.name,
    };
  });

  const handleSaveTranslations = async (items: ManageTranslationItem[]) => {
    // Translations are persisted together with the menu item on submit.
    const translations: Record<string, { name: string; description: string }> = {};
    items.forEach((item) => {
      const name = item.translation_name.trim();
      if (name) {
        translations[item.language_code] = {
          name,
          description: (item.translation_description || '').trim(),
        };
      }
    });
    setFormData((prev) => ({ ...prev, translations }));
  };

  const handleAutoTranslate = useCallback(async (text: string, sourceLanguageCode: string) => {
    const response = await api.POST<AutoTranslateResponse>(
      `${pathAPI.categoryDetail}/translations/auto`,
      { text, source_language_code: sourceLanguageCode },
      {},
      true
    );
    return response?.data?.translations || {};
  }, []);

  if (loading && isEdit) {
    return (
      <Card>
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <Typography>Loading...</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <Box className="mb-4">
        <Typography variant="h5" className="font-bold">
          {isEdit ? t('menus.edit') : t('menus.create')}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Row gutter={[16, 16]}>
          {/* Basic Information */}
          <Col xs={24} md={12}>
            <LabeledInput
              fullWidth
              label={t('menus.menuCode')}
              value={formData.code}
              onChange={handleChange('code')}
              required
              inputProps={{ required: true }}
              disabled={isEdit}
              helperText="Unique menu code (lowercase, alphanumeric + underscore)"
            />
          </Col>

          <Col xs={24} md={12}>
            <LabeledInput
              fullWidth
              type="number"
              label={t('menus.sortOrder')}
              value={formData.sort_order}
              onChange={handleChange('sort_order')}
              required
              inputProps={{ required: true }}
              helperText="Display order in menu (ascending)"
            />
          </Col>

          <Col xs={24}>
            <LabeledInput
              fullWidth
              label={t('menus.iconPath')}
              value={formData.icon_path}
              onChange={handleChange('icon_path')}
              required
              inputProps={{ required: true }}
              helperText="Path to icon asset (e.g., assets/images/commons/ic_calendar.svg)"
            />
          </Col>

          <Col xs={24}>
            <LabeledInput
              fullWidth
              label={t('menus.routeName')}
              value={formData.route_name}
              onChange={handleChange('route_name')}
              required
              inputProps={{ required: true }}
              helperText="Flutter route name (e.g., /calendarScreen)"
            />
          </Col>

          <Col xs={24} md={12}>
            <LabeledInput
              fullWidth
              label="Permission Code (optional)"
              value={formData.requires_permission || ''}
              onChange={handleChange('requires_permission')}
              helperText="Future: permission code required to access"
            />
          </Col>

          <Col xs={24} md={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleChange('is_active')}
                  color="primary"
                />
              }
              label={t('common.table.active')}
            />
          </Col>

          {/* Translations */}
          <Col xs={24}>
            <Typography variant="h6" className="mb-2">
              {t('menus.translations')}
            </Typography>
            <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={2}>
              {Object.entries(formData.translations || {}).map(([code, tr]) => (
                <Chip key={code} label={`${code.toUpperCase()}: ${tr.name}`} variant="outlined" />
              ))}
              {Object.keys(formData.translations || {}).length === 0 && (
                <Typography color="text.secondary">No translations yet</Typography>
              )}
            </Box>
            <CancelButton startIcon={<TranslateIcon />} onClick={() => setTranslationModalOpen(true)}>
              Manage Translations
            </CancelButton>
          </Col>

          {/* Action Buttons */}
          <Col xs={24}>
            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <CancelButton
                startIcon={<CancelIcon />}
                onClick={() => navigate('/settings/menus')}
                disabled={loading}
              >
                {t('btn.cancel')}
              </CancelButton>
              <ActionButton type="submit" startIcon={<SaveIcon />} disabled={loading}>
                {loading ? 'Saving...' : t('btn.save')}
              </ActionButton>
            </Box>
          </Col>
        </Row>
      </form>
      <ManageTranslationModal
        open={translationModalOpen}
        title="Manage Menu Translations"
        subtitle={formData.translations?.en?.name || formData.code}
        languages={languages || []}
        translations={translationItems}
        ready={Boolean(languages) && (!isEdit || loadedMenuId === Number(id))}
        withDescription
        onCancel={() => setTranslationModalOpen(false)}
        onSave={handleSaveTranslations}
        onAutoTranslate={handleAutoTranslate}
      />
    </Card>
  );
};

export default MenuForm;
